import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { ArrowRight, Share2, Trophy, Swords } from "lucide-react";
import {
  buildNationWarsMessage,
  buildNationWarsUrl,
  buildOgTitle,
  getNationFlag,
  getNationName,
  getNationOgImageUrl,
  getNationWarsOgDescription,
} from "@/lib/share/nationWarsShare";
import { shareNation } from "@/lib/whatsappViral/whatsappEngine";
import { useLanguage } from "@/hooks/useLanguage";
import { trackEvent } from "@/lib/audit";

export const Route = createFileRoute("/s/$nation")({
  loader: ({ params }) => ({ nation: params.nation.toUpperCase() }),
  head: ({ loaderData }) => {
    const nation = loaderData?.nation ?? null;
    const title = buildOgTitle(nation);
    const description = getNationWarsOgDescription();
    const url = `https://versesport.com/s/${(nation ?? "").toLowerCase()}`;
    const image = getNationOgImageUrl(nation);
    return {
      meta: [
        { title },
        { name: "description", content: description },
        { property: "og:title", content: title },
        { property: "og:description", content: description },
        { property: "og:type", content: "website" },
        { property: "og:url", content: url },
        { property: "og:image", content: image },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: title },
        { name: "twitter:description", content: description },
        { name: "twitter:image", content: image },
      ],
      links: [{ rel: "canonical", href: buildNationWarsUrl(nation) }],
    };
  },
  component: ShareLandingPage,
});

function ShareLandingPage() {
  const { nation } = Route.useLoaderData();
  const { lang, t } = useLanguage();
  const flag = getNationFlag(nation);
  const name = getNationName(nation, lang);
  const msg = buildNationWarsMessage(nation, lang);

  useEffect(() => {
    void trackEvent("nation_wars_share_landing_view", {
      share_target: "nation-wars",
      nation_code: nation,
    });
  }, [nation]);

  return (
    <main className="container mx-auto p-4 md:p-6 max-w-2xl space-y-5">
      <Card className="overflow-hidden border-border/60 bg-gradient-to-br from-primary/15 via-background to-accent/10">
        <CardContent className="p-6 md:p-8 text-center space-y-4">
          <div className="text-7xl leading-none">{flag}</div>
          <h1 className="text-2xl md:text-3xl font-bold">
            {t("nw_share_landing_title", { name })}
          </h1>
          <p className="text-sm md:text-base text-muted-foreground leading-relaxed max-w-lg mx-auto">
            {msg}
          </p>
          <div className="flex flex-col sm:flex-row gap-2 justify-center pt-2">
            <Button asChild size="lg" className="gap-2">
              <Link to="/nation-wars" search={{ nation }}>
                <Trophy className="h-4 w-4" /> {t("nw_share_landing_join", { name })} <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="gap-2"
              onClick={() =>
                void shareNation(
                  { nationCode: nation },
                  { source: "share_landing", lang },
                )
              }
            >
              <Share2 className="h-4 w-4" /> {t("cm_share")}
            </Button>
            <Button
              variant="secondary"
              size="lg"
              className="gap-2 bg-[#25D366] text-white hover:bg-[#1ebe5d]"
              onClick={() =>
                void shareNation(
                  { nationCode: nation },
                  { source: "share_landing", lang, preferNative: false },
                )
              }
            >
              WhatsApp
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/60">
        <CardContent className="p-5 flex items-center gap-3">
          <Swords className="h-5 w-5 text-primary" />
          <p className="text-sm">{t("nw_share_landing_blurb")}</p>
        </CardContent>
      </Card>
    </main>
  );
}